import type { KommoLead, ServiceLabel } from '../types';
import { formatBRL, formatDatetime } from '../lib/format';
import { SERVICE_COLORS } from '../types';

function WonLostBadge({ isWon, isLost }: { isWon: number; isLost: number }) {
  if (isWon) {
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontFamily: 'var(--font-mono)',
        background: 'rgba(34,197,94,0.12)',
        color: 'var(--green)',
        border: '1px solid rgba(34,197,94,0.25)',
      }}>Ganho</span>
    );
  }
  if (isLost) {
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontFamily: 'var(--font-mono)',
        background: 'rgba(239,68,68,0.12)',
        color: 'var(--red)',
        border: '1px solid rgba(239,68,68,0.25)',
      }}>Perdido</span>
    );
  }
  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontFamily: 'var(--font-mono)',
      background: 'var(--bg-tertiary)',
      color: 'var(--text-tertiary)',
      border: '1px solid var(--border-subtle)',
    }}>Em andamento</span>
  );
}

interface Props {
  leads: KommoLead[];
}

export function LeadMetricsTable({ leads }: Props) {
  if (leads.length === 0) {
    return (
      <div style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-subtle)',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center',
        color: 'var(--text-tertiary)',
        fontSize: '13px',
      }}>
        Nenhum lead encontrado
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-subtle)',
      borderRadius: '12px',
      overflow: 'hidden',
    }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              {['Lead', 'Serviço', 'Etapa', 'Valor', 'Criado em', 'Status'].map(h => (
                <th key={h} style={{
                  padding: '10px 14px',
                  textAlign: h === 'Lead' || h === 'Serviço' || h === 'Etapa' || h === 'Status' ? 'left' : 'right',
                  fontSize: '11px',
                  fontWeight: 600,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase' as const,
                  color: 'var(--text-tertiary)',
                  fontFamily: 'var(--font-sans)',
                  whiteSpace: 'nowrap',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.map((lead, i) => {
              const serviceColor = lead.service_tag
                ? SERVICE_COLORS[lead.service_tag as ServiceLabel] ?? 'var(--text-secondary)'
                : null;
              return (
                <tr
                  key={lead.id}
                  style={{ borderBottom: i < leads.length - 1 ? '1px solid var(--border-subtle)' : 'none' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ padding: '10px 14px', maxWidth: '200px' }}>
                    <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {lead.lead_name ?? `Lead #${lead.id}`}
                    </div>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    {lead.service_tag && serviceColor ? (
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontFamily: 'var(--font-mono)',
                        background: `${serviceColor}20`,
                        color: serviceColor,
                        border: `1px solid ${serviceColor}40`,
                      }}>
                        {lead.service_tag.replace('LF | ', '')}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--text-disabled)', fontSize: '12px' }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {lead.status_name}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>
                    {formatBRL(lead.sale_value)}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-secondary)' }}>
                    {formatDatetime(lead.created_at)}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <WonLostBadge isWon={lead.is_won} isLost={lead.is_lost} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
